import { merge } from "./merge.mjs";
import { getUser, oauthLogin, handleAuthCallback, refreshSession, logout } from "@netlify/identity";

// True only in the claude.ai preview build, where Google sign-in cannot run.
const PREVIEW = typeof __PREVIEW__ !== "undefined" && __PREVIEW__;

const KEY = "clear-todo-v3", V2_KEY = "clear-todo-v2", V1_KEY = "clear-todo-v1", PREFS_KEY = "clear-todo-prefs";
const SYNC_URL = "./api/sync";
const uid = () => Math.random().toString(36).slice(2, 10);
const now = () => Date.now();

/* ---------- Themes: each has a list palette; folders use their own or a derived one ---------- */
const hex = h => [1, 3, 5].map(i => parseInt(h.slice(i, i + 2), 16));
const mix = (a, b, t) => "rgb(" + a.map((v, i) => Math.round(v + (b[i] - v) * t)).join(",") + ")";
const mixHex = (a, b, t) => "#" + hex(a).map((v, i) => Math.round(v + (hex(b)[i] - v) * t).toString(16).padStart(2, "0")).join("");
const lum = h => { const [r, g, b] = hex(h); return (0.299 * r + 0.587 * g + 0.114 * b) / 255; };
const T = (name, list, folder) => ({ name, list, folder });
const L = (header, rs, re, ts, te, empty, done) => ({ header, rs, re, ts, te, empty, done });
const THEMES = {
  midnight:  T("Midnight",  L("#00214A", "#00214A", "#08576F", "#E3D196", "#8FB2A6", "#0A637C", "#0A3D55"),
                            L("#0B1A36", "#0C1F3D", "#143F61", "#E3D196", "#6E9E9E", "#133F61", "#0A2A44")),
  twilight:  T("Twilight",  L("#2A0F1C", "#2E1020", "#6E2A44", "#F4C9B8", "#E3A4A8", "#7A3350", "#4A1A2E")),
  dusk:      T("Dusk",      L("#33193F", "#3B1F4F", "#B8407A", "#FFE3EC", "#FFD0DF", "#C9548A", "#5A2E55")),
  sunrise:   T("Sunrise",   L("#F3BFE0", "#F6C9E6", "#FFE0B8", "#5A2D52", "#7A4B3A", "#FFF0DE", "#C9A9A0")),
  dawn:      T("Dawn",      L("#5E43D4", "#6A4CE0", "#F27A3A", "#FFFFFF", "#FFFFFF", "#F58D52", "#9A4A22")),
  afternoon: T("Afternoon", L("#1736C8", "#1A3FE0", "#129CE6", "#FFFFFF", "#F2FBFF", "#2FAAEC", "#1C5E86")),
  ember:     T("Heatmap",   L("#C10F27", "#D3122B", "#EE9F2E", "#FFFFFF", "#FFFFFF", "#1C1C1E", "#5C5C60")),
  heatlight: T("Heatmap light", L("#FFEDEA", "#FFE3E0", "#FFF4D6", "#C8102E", "#B86E00", "#FFFFFF", "#C9C9CC")),
  tanlines:  T("Tan lines", L("#E2AE7C", "#E7B989", "#F6E3C8", "#3E2412", "#4E3220", "#FBF0E1", "#B89A80")),
  ceramic:   T("Ceramic",   L("#E2E3E6", "#E9EAEC", "#FAFAFB", "#2B2D31", "#4A4D52", "#FFFFFF", "#B5B8BD")),
  focus:     T("Focus",     L("#FFFFFF", "#F2F2F2", "#FFFFFF", "#111111", "#333333", "#FFFFFF", "#BDBDBD")),
  othello:   T("Othello",   L("#000000", "#000000", "#2A2A2A", "#FFFFFF", "#D0D0D0", "#FFFFFF", "#9A9A9A")),
  blackbird: T("Blackbird", L("#0B0B0C", "#0E0E10", "#2B2C30", "#E8E8EA", "#A9AAB0", "#2F3034", "#55565C")),
  simplewhite: T("Simple white", L("#FFFFFF", "#FFFFFF", "#F1F2F4", "#1F2328", "#57606A", "#F6F7F8", "#B0B6BE")),
  moss:      T("Moss",      L("#12301F", "#12301F", "#3B7548", "#EAD9A7", "#BBD6B1", "#4A8656", "#244A30"),
                            L("#0C1F14", "#10281A", "#24503A", "#EAD9A7", "#9DC2A9", "#26543C", "#183A26")),
  graphite:  T("Graphite",  L("#1B1D21", "#1B1D21", "#4A5058", "#F2F2F2", "#C9CDD2", "#596068", "#2C3036"),
                            L("#121417", "#17191C", "#33373D", "#F2F2F2", "#AEB3BA", "#363A40", "#24272B"))
};
const THEME_ORDER = Object.keys(THEMES);
function palette(key, kind) {
  const t = THEMES[key] || THEMES.midnight, l = t.list;
  const p = kind === "list" ? l : (t.folder || L(l.header, l.rs, mixHex(l.rs, l.re, 0.55), l.ts, l.te, mixHex(l.rs, l.re, 0.6), l.done));
  const light = lum(p.header) > 0.6;
  return Object.assign({ light, pull: mixHex(p.header, light ? "#000000" : "#000000", light ? 0.06 : 0.3) }, p);
}

/* ---------- Fonts and sizes ---------- */
const SYS = '-apple-system,BlinkMacSystemFont,"SF Pro Text","Segoe UI",Inter,Roboto,"Helvetica Neue",Arial,sans-serif';
const FONTS = {
  system:     { name: "System",        stack: SYS },
  rounded:    { name: "System rounded", stack: 'ui-rounded,"SF Pro Rounded",' + SYS },
  helvlight:  { name: "Helvetica light", stack: '"Helvetica Neue",Helvetica,Arial,sans-serif', row: 300, bold: 400 },
  avenir:     { name: "Avenir",        stack: '"Avenir Next",Avenir,"Segoe UI",' + SYS, bold: 600 },
  futura:     { name: "Futura",        stack: 'Futura,"Century Gothic","Trebuchet MS",sans-serif', bold: 600 },
  serif:      { name: "Serif",         stack: 'ui-serif,"New York",Georgia,"Times New Roman",serif' },
  typewriter: { name: "Typewriter",    stack: '"American Typewriter","Courier New",Courier,monospace', row: 400, bold: 600 },
  mono:       { name: "Mono",          stack: 'ui-monospace,"SF Mono",Menlo,Consolas,"Courier New",monospace', row: 400, bold: 600 },
  marker:     { name: "Marker",        stack: '"Marker Felt","Comic Sans MS","Chalkboard SE",cursive', row: 400, bold: 400 }
};
const FONT_ORDER = Object.keys(FONTS);
const SIZES = { nano: ["Nano", 0.72], micro: ["Micro", 0.82], tiny: ["Tiny", 0.91], small: ["Small", 1], medium: ["Medium", 1.1], large: ["Large", 1.22] };
const SIZE_ORDER = Object.keys(SIZES);

/* ---------- Preferences (this device only, never synced) ---------- */
const DEFAULT_PREFS = { theme: "midnight", size: "small", font: "system", account: "", lastSync: 0 };
const RETIRED_PREFS = ["showDone", "haptics", "sounds", "counts", "tapBack", "tapAdd", "pullBack", "showCrumb", "badge"];
function loadPrefs() {
  try { return Object.assign({}, DEFAULT_PREFS, JSON.parse(localStorage.getItem(PREFS_KEY)) || {}); }
  catch (e) { return Object.assign({}, DEFAULT_PREFS); }
}
let prefs = loadPrefs();
if (!THEMES[prefs.theme]) prefs.theme = "midnight";
if (prefs.size === "standard") prefs.size = "small";
if (!SIZES[prefs.size]) prefs.size = "small";
if (!FONTS[prefs.font]) prefs.font = "system";
delete prefs.syncToken; // the old shared-password sync is gone
RETIRED_PREFS.forEach(k => delete prefs[k]); // these settings were removed
function savePrefs() { try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch (e) {} }

/* ---------- Data ---------- */
function seed() {
  const nodes = {};
  const root = { id: "root", type: "folder", title: "My lists", parent: null, children: [], updatedAt: 1, orderAt: 1 };
  nodes.root = root;
  const list = (id, title, texts, parent) => {
    const n = { id, type: "list", title, parent: parent.id, updatedAt: 1, orderAt: 1,
      items: texts.map((t, i) => ({ id: id + "-" + i, text: t, done: false, doneAt: null, updatedAt: 1 })) };
    nodes[id] = n; parent.children.push(id); return n;
  };
  const folder = (id, title, parent) => {
    const n = { id, type: "folder", title, parent: parent.id, children: [], updatedAt: 1, orderAt: 1 };
    nodes[id] = n; parent.children.push(id); return n;
  };
  const med = list("seed-medical", "Medical", ["Netsuite", "Sid list", "Update med xlsx", "Todo app like clear app", "Add password for netlify", "Support Sid"], root);
  let old = null;
  try { old = JSON.parse(localStorage.getItem(V1_KEY)); } catch (e) {}
  if (old && Array.isArray(old.items)) { med.items = old.items.map(i => ({ ...i, updatedAt: 2 })); med.title = old.title || med.title; }
  list("seed-work", "Work", ["Draft the discovery brief", "Prep Thursday playback"], root);
  const personal = folder("seed-personal", "Personal", root);
  list("seed-groceries", "Groceries", ["Oat milk", "Lemons", "Coffee beans"], personal);
  list("seed-weekend", "Weekend", ["Book the garage", "Call Mum"], personal);
  return { v: 3, nodes, current: med.id };
}
function load() {
  try {
    const s = JSON.parse(localStorage.getItem(KEY));
    if (s && s.nodes && s.nodes.root) return s;
  } catch (e) {}
  try {
    const s2 = JSON.parse(localStorage.getItem(V2_KEY));
    if (s2 && s2.nodes && s2.nodes.root) { // upgrade: add timestamps so merging works
      Object.values(s2.nodes).forEach(n => {
        n.updatedAt = 2; n.orderAt = 2;
        if (n.items) n.items.forEach(i => { i.updatedAt = 2; });
      });
      return { v: 3, nodes: s2.nodes, current: s2.current };
    }
  } catch (e) {}
  return seed();
}
let state = load();
function saveLocal() { try { localStorage.setItem(KEY, JSON.stringify(state)); } catch (e) {} }
function save() { saveLocal(); scheduleSync(); }

const PAGES = {
  "settings": { id: "settings", type: "settings", title: "Settings", parent: "root" },
  "settings-personalise": { id: "settings-personalise", type: "settings", title: "Personalise", parent: "root" },
  "settings-themes": { id: "settings-themes", type: "settings", title: "Themes", parent: "settings-personalise" },
  "settings-fonts": { id: "settings-fonts", type: "settings", title: "Fonts", parent: "settings-personalise" }
};
const SETTINGS = PAGES.settings;
const ROOT_PAGES = [PAGES["settings-personalise"], SETTINGS]; // pinned to the bottom of My lists
const N = () => state.nodes;
const getNode = id => PAGES[id] || N()[id];
const alive = n => n && !n.deleted;
const liveItems = n => (n.items || []).filter(i => !i.deleted);
// A task whose text starts with a space is a separator: a short, bold heading row.
const isSeparator = it => !!it && typeof it.text === "string" && it.text.startsWith(" ") && it.text.trim() !== "";
const isTask = it => !isSeparator(it);
const liveChildren = n => (n.children || []).map(getNode).filter(alive);
let cur = alive(getNode(state.current)) ? state.current : "root";
const node = () => getNode(cur);
const viewType = () => node().type;
const findItem = id => (node().items || []).find(i => i.id === id) || null;
const entry = id => viewType() === "list" ? findItem(id) : getNode(id);
function isAncestor(a, b) { let n = getNode(b); while (n && n.parent) { if (n.parent === a) return true; n = getNode(n.parent); } return false; }

/* ---------- DOM ---------- */
const $ = id => document.getElementById(id);
const root = document.documentElement, main = $("main"), view = $("view");
const activeUl = $("active"), doneUl = $("done"), filler = $("filler"), emptyHint = $("empty-hint"), backHint = $("back-hint");
const pullEl = $("pull"), pullText = pullEl.firstElementChild, titleEl = $("title"), crumb = $("crumb"), titleIcon = $("title-icon");
const toast = $("toast"), toastMsg = $("toast-msg"), toastUndo = $("toast-undo");
const themeMeta = document.querySelector('meta[name="theme-color"]');
const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function buzz() { try { if (navigator.vibrate) navigator.vibrate(10); } catch (e) {} }
const rowH = () => { const r = activeUl.querySelector(".row:not(.separator)"); return r ? r.offsetHeight : 48; };
// Top edge of each row plus the bottom of the last, relative to the list (ignores transforms).
const rowEdges = rows => rows.length ? rows.map(r => r.offsetTop).concat(rows[rows.length - 1].offsetTop + rows[rows.length - 1].offsetHeight) : [0];
function nearestEdge(edges, y) { let b = 0; edges.forEach((v, i) => { if (Math.abs(v - y) < Math.abs(edges[b] - y)) b = i; }); return b; }
const threshold = () => Math.min(80, window.innerWidth * 0.3);

const TICK = '<svg class="icon icon-tick" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 12.5l5 5L20 6"/></svg>';
const PENCIL = '<svg class="icon icon-pencil" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 20l1-5L16 4l4 4L9 19z"/></svg>';
const CROSS = '<svg class="icon icon-cross" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.6" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
const FOLDER = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linejoin="round" aria-hidden="true"><path d="M3 6.5h6l2 2h10v10H3z"/></svg>';
const BRUSH = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="5" y="2.5" width="11" height="15" rx="2"/><path d="M8 7h5M8 10h5M8 13h3"/><path d="M21 12.5l-5.5 5.5"/><path d="M15.5 18c-1.4 0-2.5 1.1-2.5 2.5V21.5h1c1.4 0 2.5-1.1 2.5-2.5z"/></svg>';
const GEAR = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="3.5"/><path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.3 5.3l2.1 2.1M16.6 16.6l2.1 2.1M5.3 18.7l2.1-2.1M16.6 7.4l2.1-2.1"/></svg>';

function swatch(key) {
  const p = palette(key, "list"), w = [14, 10, 12, 8];
  const bands = [0, 1, 2, 3].map(i => '<rect x="0" y="' + i * 7.5 + '" width="30" height="7.6" fill="' + mixHex(p.rs, p.re, i / 3) + '"/>'
    + '<rect x="5" y="' + (i * 7.5 + 3) + '" width="' + w[i] + '" height="1.7" rx=".85" fill="' + mixHex(p.ts, p.te, i / 3) + '"/>').join("");
  return '<span class="tile"><svg viewBox="0 0 30 30" aria-hidden="true">' + bands + '</svg></span>';
}
function aaTile(key) {
  const f = FONTS[key];
  return '<span class="tile" style="font-family:' + f.stack.replace(/"/g, "&quot;") + ';font-weight:' + (f.row || 500) + '">Aa</span>';
}
const plainTile = svg => '<span class="tile plain">' + svg + '</span>';

const els = new Map();
function clearRows() { els.forEach(li => li.remove()); els.clear(); }

function rowEl(e, kind) {
  let li = els.get(e.id);
  if (!li) {
    li = document.createElement("li");
    li.className = "row"; li.dataset.id = e.id; li.tabIndex = 0;
    li.innerHTML = '<div class="reveal">' + TICK + PENCIL + CROSS + '</div><div class="content"><span class="label"></span><span class="meta"></span></div>';
    els.set(e.id, li);
  }
  const sep = kind === "item" && isSeparator(e);
  const label = kind === "item" ? (sep ? e.text.trim() : e.text) : e.title;
  li.classList.toggle("separator", sep);
  if (!li.classList.contains("editing") && kind !== "pref") li.querySelector(".label").textContent = label;
  const meta = li.querySelector(".meta");
  if (kind === "item") {
    meta.innerHTML = ""; li.setAttribute("aria-label", label + (sep ? ", separator" : e.done ? ", completed" : ""));
  } else if (kind === "pref") {
    li.classList.toggle("tall", !!e.sub);
    li.classList.toggle("section", e.kind === "section");
    li.classList.toggle("on", !!e.on);
    if (!li.classList.contains("editing")) {
      const c = li.querySelector(".content");
      c.innerHTML = (e.icon || "") + '<span class="label"><span class="t"></span>' + (e.sub ? '<span class="sub"></span>' : "") + '</span><span class="meta"></span>';
      c.querySelector(".t").textContent = e.title;
      if (e.sub) c.querySelector(".sub").textContent = e.sub;
      c.querySelector(".meta").textContent = e.value || "";
      c.style.fontFamily = e.font || "";
    }
    const state_ = e.kind === "toggle" ? (e.on ? ", on" : ", off") : e.kind === "choice" && e.on ? ", selected" : "";
    li.setAttribute("aria-label", e.title + state_ + (e.value ? ", " + e.value : ""));
    if (e.kind === "section") li.removeAttribute("tabindex"); else li.tabIndex = 0;
  } else if (e.type === "settings") {
    meta.innerHTML = e.id === "settings" ? GEAR : BRUSH; li.setAttribute("aria-label", e.title);
  } else if (e.type === "folder") {
    const c = liveChildren(e).length;
    meta.innerHTML = FOLDER + "<span>" + c + "</span>"; li.setAttribute("aria-label", label + ", folder, " + c + " inside");
  } else {
    const open = liveItems(e).filter(i => !i.done && isTask(i)).length;
    meta.textContent = String(open); li.setAttribute("aria-label", label + ", list, " + open + " to do");
  }
  return li;
}

function applyPrefs(type) {
  const P = palette(prefs.theme, type === "list" ? "list" : "folder");
  const st = root.style, f = FONTS[prefs.font];
  st.setProperty("--header", P.header); st.setProperty("--bg-empty", P.empty); st.setProperty("--text-title", P.ts);
  st.setProperty("--text-done", P.done); st.setProperty("--pull", P.pull);
  st.setProperty("--scale", SIZES[prefs.size][1]);
  st.setProperty("--font", f.stack); st.setProperty("--w-row", f.row || 500); st.setProperty("--w-bold", f.bold || 700);
  st.colorScheme = P.light ? "light" : "dark";
  themeMeta.setAttribute("content", P.header);
  return P;
}

let needsRender = false;
function render(opts) {
  if (g && g.lock) { needsRender = true; return; }
  needsRender = false;
  const flip = !(opts && opts.flip === false) && !reduce;
  const first = new Map();
  if (flip) els.forEach((li, id) => { if (li.isConnected) first.set(id, li.getBoundingClientRect().top); });

  const n = node(), type = n.type;
  root.classList.toggle("list-view", type === "list");
  root.classList.toggle("folder-view", type !== "list");
  root.classList.toggle("settings-view", type === "settings");
  const P = applyPrefs(type);
  const rs = hex(P.rs), re = hex(P.re), ts = hex(P.ts), te = hex(P.te);

  let active = [], done = [], kind = "node";
  if (type === "list") {
    kind = "item";
    const items = liveItems(n);
    active = items.filter(i => !i.done);
    done = items.filter(i => i.done).sort((a, b) => b.doneAt - a.doneAt);
    activeUl.setAttribute("aria-label", "Tasks");
  } else if (type === "settings") {
    kind = "pref"; active = settingsRows(n.id); activeUl.setAttribute("aria-label", n.title);
  } else {
    active = liveChildren(n);
    if (n.id === "root") active = active.concat(ROOT_PAGES);
    activeUl.setAttribute("aria-label", "Lists and folders");
  }
  const span = Math.max(active.length - 1, type === "list" ? 5 : 9);
  active.forEach((e, i) => {
    const li = rowEl(e, kind), t = i / span;
    li.style.setProperty("--bg", mix(rs, re, t));
    li.style.setProperty("--fg", mix(ts, te, t));
    activeUl.appendChild(li);
  });
  done.forEach(e => doneUl.appendChild(rowEl(e, "item")));
  const live = new Set(active.concat(done).map(e => e.id));
  els.forEach((li, id) => { if (!live.has(id)) { li.remove(); els.delete(id); } });
  const selectable = new Set(type === "settings" ? [] : active.filter(e => !PAGES[e.id]).map(e => e.id));
  selected.forEach(id => { if (!selectable.has(id)) selected.delete(id); });
  paintSelection();

  if (!titleEl.querySelector("input")) titleEl.textContent = n.title;
  titleIcon.innerHTML = n.id === "settings" ? plainTile(GEAR) : n.id === "settings-personalise" ? plainTile(BRUSH)
    : n.id === "settings-themes" ? swatch(prefs.theme) : n.id === "settings-fonts" ? aaTile(prefs.font) : "";
  const parent = n.parent ? getNode(n.parent) : null;
  crumb.textContent = parent ? "\u2039 " + parent.title : "";
  document.title = n.title;
  emptyHint.textContent =
    type === "list" ? (liveItems(n).length ? "" : "Pull down to add a task.")
    : type === "settings" ? ""
    : (active.length > (n.id === "root" ? ROOT_PAGES.length : 0) ? "" : "Pull down to create a list. Pull further for a folder.");
  backHint.textContent = parent ? "Tap here to go back to " + parent.title : "";
  filler.classList.toggle("can-up", !!parent);

  if (flip) {
    els.forEach((li, id) => {
      const f = first.get(id);
      if (f == null) return;
      const dy = f - li.getBoundingClientRect().top;
      if (Math.abs(dy) < 1) return;
      li.style.transition = "none";
      li.style.transform = "translateY(" + dy + "px)";
      requestAnimationFrame(() => requestAnimationFrame(() => {
        li.style.transition = "transform .25s ease";
        li.style.transform = "";
        setTimeout(() => { li.style.transition = ""; }, 300);
      }));
    });
  }
}
const flushRender = () => { if (needsRender) render(); };

/* ---------- Navigation (mirrored in the hash so Back works) ---------- */
const hashFor = id => "#n-" + id;
function idFromHash() { const m = /^#n-([a-z0-9-]+)$/.exec(location.hash); return m && alive(getNode(m[1])) ? m[1] : null; }
function go(id, push) {
  if (!alive(getNode(id)) || id === cur) return;
  if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
  const dir = isAncestor(id, cur) ? "in-up" : "in-down";
  cur = id; if (!PAGES[id]) state.current = id; saveLocal();
  selected.clear(); anchor = null; gapAt = null; insertEl.classList.remove("show");
  clearRows(); render({ flip: false });
  main.scrollTop = 0;
  if (!reduce) { view.classList.remove("in-up", "in-down"); void view.offsetWidth; view.classList.add(dir); }
  if (push !== false && location.hash !== hashFor(id)) location.hash = hashFor(id);
}
function goUp() { const p = node().parent; if (p) go(p); }
window.addEventListener("hashchange", () => { const id = idFromHash(); if (id) go(id, false); });
view.addEventListener("animationend", () => view.classList.remove("in-up", "in-down"));
filler.addEventListener("click", () => {
  if (clearSelection()) return;
  goUp();
});
crumb.addEventListener("click", goUp);

/* ---------- Messages, and undo for deletes ---------- */
let undoList = null, toastTimer = null;
function showToast(msg, undo) {
  undoList = undo || null;
  toastMsg.textContent = msg;
  toastUndo.hidden = !undoList;
  toast.classList.add("show");
  clearTimeout(toastTimer);
  toastTimer = setTimeout(hideToast, undoList ? 5000 : 4000);
}
function offerUndo(flipped, label) { showToast("Deleted \u201C" + label + "\u201D", flipped); }
function hideToast() { toast.classList.remove("show"); undoList = null; }
toastUndo.addEventListener("click", e => {
  e.stopPropagation();
  if (!undoList) return;
  const t = now();
  undoList.forEach(f => {
    if (f.node) { const c = N()[f.node]; if (c) { c.deleted = false; c.updatedAt = t; } }
    else { const l = N()[f.list], it = l && l.items.find(i => i.id === f.item); if (it) { it.deleted = false; it.updatedAt = t; } }
  });
  save(); hideToast(); render({ flip: false });
});

function resetRow(li) {
  li.classList.remove("dir-right", "dir-left", "past", "dragging");
  li.style.removeProperty("--p");
  li.querySelector(".content").style.transform = "";
}

/* ---------- Actions (every change is timestamped so devices can merge) ---------- */
function toggleDone(id) {
  const n = node(), it = findItem(id), t = now();
  if (!it) return;
  if (it.done) {
    it.done = false; it.doneAt = null; it.updatedAt = t;
    n.items.splice(n.items.indexOf(it), 1); n.items.push(it); n.orderAt = t;
  } else { it.done = true; it.doneAt = t; it.updatedAt = t; }
  save(); render();
}

function tombstone(id, t, flipped) {
  const c = N()[id];
  if (!c || c.deleted) return;
  c.deleted = true; c.updatedAt = t; flipped.push({ node: id });
  if (c.type === "folder") c.children.forEach(k => tombstone(k, t, flipped));
}

function removeEntry(id, opts) {
  const n = node();
  if (n.type === "settings" || PAGES[id]) return;
  const e = entry(id);
  if (!alive(e)) return;
  const label = n.type === "list" ? e.text : e.title;
  const finish = () => {
    const t = now(), flipped = [];
    if (n.type === "list") {
      const it = findItem(id);
      if (it && !it.deleted) { it.deleted = true; it.updatedAt = t; flipped.push({ list: n.id, item: id }); }
    } else tombstone(id, t, flipped);
    save(); render({ flip: false });
    if (!(opts && opts.silent)) offerUndo(flipped, label);
  };
  const li = els.get(id);
  if (!li || reduce || (opts && opts.instant)) { finish(); return; }
  li.style.height = li.offsetHeight + "px";
  li.classList.add("collapse");
  requestAnimationFrame(() => { li.style.height = "0px"; });
  setTimeout(finish, 210);
}

function addEntry(where, kind, beforeId) {
  const n = node(), id = uid(), t = now();
  if (n.type === "settings") return;
  if (n.type === "list") {
    const it = { id, text: "", done: false, doneAt: null, updatedAt: t };
    const at = where === "before" ? n.items.findIndex(x => x.id === beforeId) : -1;
    if (where === "top") n.items.unshift(it); else if (at >= 0) n.items.splice(at, 0, it); else n.items.push(it);
  } else {
    N()[id] = kind === "folder"
      ? { id, type: "folder", title: "", parent: cur, children: [], updatedAt: t, orderAt: t }
      : { id, type: "list", title: "", parent: cur, items: [], updatedAt: t, orderAt: t };
    const at = where === "before" ? n.children.indexOf(beforeId) : -1;
    if (where === "top") n.children.unshift(id); else if (at >= 0) n.children.splice(at, 0, id); else n.children.push(id);
  }
  n.orderAt = t;
  render({ flip: false });
  startEdit(id, true);
}

function inlineInput(li, opts) {
  li.classList.add("editing");
  const label = li.querySelector(".label");
  const input = document.createElement("input");
  input.className = "edit"; input.value = opts.value || ""; input.enterKeyHint = "done"; input.autocomplete = "off";
  if (opts.type) input.type = opts.type;
  input.setAttribute("aria-label", opts.aria); input.placeholder = opts.placeholder || "";
  label.textContent = ""; label.appendChild(input);
  input.focus();
  li.scrollIntoView({ block: "nearest" });
  let finished = false;
  input.addEventListener("keydown", ev => {
    ev.stopPropagation();
    if (ev.key === "Enter") { ev.preventDefault(); input.blur(); }
    else if (ev.key === "Escape") { input.dataset.cancel = "1"; input.blur(); }
  });
  input.addEventListener("blur", () => {
    if (finished) return; finished = true;
    li.classList.remove("editing");
    opts.done(input.dataset.cancel ? null : opts.keepLeadingSpace ? input.value.trimEnd() : input.value.trim());
  });
}

function startEdit(id, isNew) {
  const li = els.get(id), listView = viewType() === "list", e = entry(id);
  if (!li || !alive(e) || viewType() === "settings" || (listView && e.done) || li.classList.contains("editing")) return;
  const original = listView ? e.text : e.title;
  inlineInput(li, {
    value: original,
    aria: listView ? "Task" : (e.type === "folder" ? "Folder name" : "List name"),
    placeholder: listView ? "" : (e.type === "folder" ? "New folder" : "New list"),
    keepLeadingSpace: listView,
    done: v => {
      const fresh = entry(id); // data may have been replaced by a sync while typing
      if (!fresh) { render({ flip: false }); return; }
      if (v === null) v = isNew ? "" : original;
      if (!v.trim()) {
        if (isNew) { fresh.deleted = true; fresh.updatedAt = now(); save(); render({ flip: false }); }
        else if (listView) removeEntry(id, { instant: true });
        else render({ flip: false });
        return;
      }
      if (listView) fresh.text = v; else fresh.title = v;
      fresh.updatedAt = now();
      save(); render();
    }
  });
}

function editTitle() {
  if (titleEl.querySelector("input") || viewType() === "settings") return;
  const input = document.createElement("input");
  input.className = "edit"; input.value = node().title; input.setAttribute("aria-label", "Name");
  titleEl.textContent = ""; titleEl.appendChild(input); input.focus();
  input.addEventListener("keydown", ev => {
    ev.stopPropagation();
    if (ev.key === "Enter") input.blur();
    if (ev.key === "Escape") { input.value = node().title; input.blur(); }
  });
  input.addEventListener("blur", () => {
    const v = input.value.trim(), n = node();
    input.remove();
    if (v && v !== n.title) { n.title = v; n.updatedAt = now(); save(); }
    render({ flip: false });
  });
}
titleEl.addEventListener("click", editTitle);

/* ---------- Settings ---------- */
let syncStatus = "";
function ago(t) {
  const s = Math.round((now() - t) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return Math.round(s / 60) + " min ago";
  if (s < 86400) return Math.round(s / 3600) + " h ago";
  return new Date(t).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}
function syncLabel() {
  if (!prefs.account) return "Off";
  if (syncStatus === "syncing") return "Syncing\u2026";
  if (syncStatus === "expired") return "Sign in again";
  if (syncStatus === "missing") return "Not on Netlify";
  if (syncStatus === "error") return "Can\u2019t reach server";
  if (syncStatus === "offline") return "Offline";
  return prefs.lastSync ? "Synced " + ago(prefs.lastSync) : "On";
}
let prefRows = new Map();
const section = (id, title) => ({ id, title, kind: "section" });
function settingsRows(page) {
  let rows = [];
  if (page === "settings") {
    rows = [
      section("sec-sync", "Sync")
    ];
    if (prefs.account) {
      rows.push({ id: "pref-account", title: "Signed in with Google", sub: prefs.account, kind: "info" });
      rows.push({ id: "pref-sync", title: "Sync now", kind: "action", value: syncLabel(),
        onTap: () => { if (syncStatus === "expired") signIn(); else syncNow(); return "stay"; } });
      rows.push({ id: "pref-signout", title: "Sign out", sub: "Your lists stay on this device", kind: "action",
        onTap: () => { signOut(); return "stay"; } });
    } else {
      rows.push({ id: "pref-signin", title: "Sign in with Google", sub: "Sync your lists across devices", kind: "action",
        onTap: () => { signIn(); return "stay"; } });
    }
    rows.push({ id: "pref-export", title: "Download a backup", kind: "action", onTap: () => { exportBackup(); return "stay"; } });
  } else if (page === "settings-personalise") {
    rows = [
      { id: "nav-themes", title: "Themes", kind: "nav", icon: swatch(prefs.theme), value: THEMES[prefs.theme].name, onTap: () => go("settings-themes") },
      { id: "nav-fonts", title: "Fonts", kind: "nav", icon: aaTile(prefs.font), value: SIZES[prefs.size][0], onTap: () => go("settings-fonts") }
    ];
  } else if (page === "settings-themes") {
    rows = THEME_ORDER.map(k => ({ id: "theme-" + k, title: THEMES[k].name, kind: "choice", on: prefs.theme === k, icon: swatch(k),
      onTap: () => { prefs.theme = k; } }));
  } else if (page === "settings-fonts") {
    rows = [section("sec-size", "Size")]
      .concat(SIZE_ORDER.map(k => ({ id: "size-" + k, title: SIZES[k][0], kind: "choice", on: prefs.size === k, onTap: () => { prefs.size = k; } })))
      .concat([section("sec-font", "Font")])
      .concat(FONT_ORDER.map(k => ({ id: "font-" + k, title: FONTS[k].name, kind: "choice", on: prefs.font === k, icon: aaTile(k),
        font: FONTS[k].stack, onTap: () => { prefs.font = k; } })));
  }
  prefRows = new Map(rows.map(r => [r.id, r]));
  return rows;
}
function handlePref(id) {
  const r = prefRows.get(id);
  if (!r || !r.onTap) return;
  const res = r.onTap(els.get(id));
  if (res === "stay" || r.kind === "nav") return;
  buzz();
  savePrefs(); render({ flip: false });
}
function exportBackup() {
  const blob = new Blob([JSON.stringify({ v: 3, nodes: state.nodes }, null, 2)], { type: "application/json" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "todo-backup-" + new Date().toISOString().slice(0, 10) + ".json";
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

/* ---------- Google sign-in (Netlify Identity) ---------- */
function signIn() {
  if (PREVIEW) { showToast("Google sign-in works once the app is on Netlify"); return; }
  if (!navigator.onLine) { showToast("Connect to the internet to sign in"); return; }
  try { oauthLogin("google"); }
  catch (e) { showToast("Sign-in isn\u2019t set up on this site yet"); }
}
async function signOut(quiet) {
  try { await logout(); } catch (e) {}
  prefs.account = ""; prefs.lastSync = 0; syncStatus = ""; savePrefs();
  render({ flip: false });
  if (!quiet) showToast("Signed out. Your lists are still on this device.");
}
const AUTH_HASH = /^#(access_token|error|confirmation_token|invite_token|recovery_token|email_change_token)=/;
async function finishSignIn() {
  const raw = location.hash;
  if (!AUTH_HASH.test(raw)) return false;
  const params = new URLSearchParams(raw.slice(1));
  try {
    if (params.get("error")) throw new Error(params.get("error_description") || params.get("error"));
    const result = await handleAuthCallback();
    const email = result && result.user && result.user.email;
    if (email) { prefs.account = email; syncStatus = ""; savePrefs(); showToast("Signed in as " + email); }
  } catch (e) {
    showToast("Google sign-in didn\u2019t finish: " + ((e && e.message) || "please try again"));
  }
  return true;
}
function checkAccount() {
  getUser().then(u => {
    if (u && u.email) {
      if (u.email !== prefs.account) { prefs.account = u.email; savePrefs(); if (viewType() === "settings") render({ flip: false }); }
    } else if (prefs.account && navigator.onLine) setSyncStatus("expired");
  }).catch(() => {});
}

/* ---------- Sync with Netlify ---------- */
let syncTimer = null, syncing = false, syncAgain = false;
function setSyncStatus(s) { syncStatus = s; if (cur === "settings") render({ flip: false }); }
function scheduleSync(delay) {
  if (!prefs.account || PREVIEW) return;
  clearTimeout(syncTimer);
  syncTimer = setTimeout(syncNow, delay == null ? 1500 : delay);
}
function applyRemote(doc) {
  if (!doc || !doc.nodes) return;
  const before = JSON.stringify(state.nodes);
  const merged = merge(doc, { v: 3, nodes: state.nodes });
  if (JSON.stringify(merged.nodes) === before) return;
  state.nodes = merged.nodes; saveLocal();
  if (!alive(getNode(cur))) { cur = "root"; clearRows(); history.replaceState(null, "", hashFor(cur)); }
  render({ flip: false });
}
async function syncNow() {
  if (!prefs.account || PREVIEW) return;
  if (syncing) { syncAgain = true; return; }
  if (!navigator.onLine) { setSyncStatus("offline"); return; }
  syncing = true; setSyncStatus("syncing");
  try {
    try { await refreshSession(); } catch (e) {} // keep the sign-in cookie fresh
    const res = await fetch(SYNC_URL, {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ doc: { v: 3, nodes: state.nodes } })
    });
    if (res.status === 401) { setSyncStatus("expired"); return; }
    if (res.status === 403) {
      const who = prefs.account;
      await signOut(true);
      showToast((who || "That account") + " isn\u2019t allowed to sync");
      return;
    }
    if (res.status === 404) { setSyncStatus("missing"); return; }
    if (!res.ok) throw new Error("HTTP " + res.status);
    const body = await res.json();
    applyRemote(body.doc);
    prefs.lastSync = now(); savePrefs();
    setSyncStatus("ok");
  } catch (err) {
    setSyncStatus(navigator.onLine ? "error" : "offline");
  } finally {
    syncing = false;
    if (syncAgain) { syncAgain = false; scheduleSync(300); }
  }
}
window.addEventListener("online", () => scheduleSync(200));
window.addEventListener("offline", () => { if (prefs.account) setSyncStatus("offline"); });
document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") scheduleSync(200); });
setInterval(() => {
  if (document.visibilityState !== "visible") return;
  scheduleSync(0);
}, 60000);
setInterval(() => { if (cur === "settings" && !g) render({ flip: false }); }, 30000);

/* ---------- Gestures ---------- */
let g = null;
const isFixedRow = id => !!PAGES[id] || viewType() === "settings";

main.addEventListener("pointerdown", e => {
  const li = e.target.closest(".row");
  if (!li || li.classList.contains("editing") || g) return;
  if (e.pointerType === "mouse" && e.button !== 0) return;
  const isDone = li.parentElement === doneUl, fixed = isFixedRow(li.dataset.id);
  g = { li, id: li.dataset.id, x0: e.clientX, y0: e.clientY, dx: 0, dy: 0, lock: null,
        pid: e.pointerId, type: e.pointerType, isDone, fixed, past: false, pull: 0, lp: null,
        shift: e.shiftKey, multi: e.ctrlKey || e.metaKey };
  setGap(null);
  // Touch: long-press to reorder. Mouse: press and drag instead (see startMultiDrag).
  if (!isDone && !fixed && e.pointerType !== "mouse") g.lp = setTimeout(startReorder, 450);
});

window.addEventListener("pointermove", e => {
  if (!g || e.pointerId !== g.pid) return;
  g.dx = e.clientX - g.x0; g.dy = e.clientY - g.y0;
  if (g.lock === "r") { moveReorder(); return; }
  if (g.lock === "m") { moveMultiDrag(e); return; }
  if (!g.lock) {
    if (Math.abs(g.dx) > 10 && Math.abs(g.dx) > Math.abs(g.dy)) {
      clearTimeout(g.lp);
      if (g.fixed) { g.lock = "x"; return; }
      g.lock = "h"; g.li.classList.add("dragging");
      try { g.li.setPointerCapture(g.pid); } catch (err) {}
    } else if (Math.abs(g.dy) > 10) {
      g.lock = "v"; clearTimeout(g.lp);
    } else return;
  }
  if (g.lock === "h") dragH();
  else if (g.lock === "v" && g.type === "mouse") {
    if (!g.isDone && !g.fixed && selectableView()) { startMultiDrag(); moveMultiDrag(e); }
    else if (main.scrollTop <= 0) { g.pull = Math.max(0, g.dy * 0.5); setPull(g.pull); }
  }
});

function dragH() {
  const li = g.li, dx = g.dx, th = threshold();
  li.querySelector(".content").style.transform = "translateX(" + dx + "px)";
  li.classList.toggle("dir-right", dx > 0);
  li.classList.toggle("dir-left", dx < 0);
  li.style.setProperty("--p", Math.min(Math.abs(dx) / th, 1));
  const past = Math.abs(dx) >= th;
  if (past !== g.past) { g.past = past; li.classList.toggle("past", past); if (past) buzz(); }
}

function end(e, cancelled) {
  if (!g || e.pointerId !== g.pid) return;
  clearTimeout(g.lp);
  const c = g; g = null;
  if (c.lock === "r") { endReorder(c); return; }
  if (c.lock === "m") { endMultiDrag(c, cancelled); return; }
  if (c.lock === "h") {
    const content = c.li.querySelector(".content");
    c.li.classList.remove("dragging");
    if (c.past && !cancelled) {
      if (c.dx > 0) {
        content.style.transform = "";
        if (viewType() === "list") setTimeout(() => { toggleDone(c.id); resetRow(c.li); }, 200);
        else { startEdit(c.id); setTimeout(() => resetRow(c.li), 200); }
      } else {
        content.style.transform = "translateX(" + (-c.li.offsetWidth) + "px)";
        setTimeout(() => removeEntry(c.id), 190);
      }
    } else {
      content.style.transform = "";
      setTimeout(() => { resetRow(c.li); flushRender(); }, 210);
    }
    return;
  }
  if (c.lock === "v" && c.type === "mouse") { releasePull(c.pull); flushRender(); return; }
  if (!c.lock && !cancelled) {
    if (viewType() === "settings") handlePref(c.id);
    else if (PAGES[c.id]) go(c.id);
    else if (c.type === "mouse" && !c.isDone && selectableView()) {
      // First click selects; clicking the only selected item again edits or opens it.
      if (selectClick(c.id, c.shift, c.multi) === "activate") { if (viewType() === "list") startEdit(c.id); else go(c.id); }
    }
    else if (viewType() === "list") startEdit(c.id);
    else go(c.id);
  }
  flushRender();
}
window.addEventListener("pointerup", e => end(e, false));
window.addEventListener("pointercancel", e => end(e, true));

/* ---------- Mouse: selection, insert gap, drag to reorder ---------- */
const insertEl = $("insert"), ghost = $("ghost");
let selected = new Set(), anchor = null, gapAt = null, gapTimer = null;
const orderableRows = () => Array.from(activeUl.children).filter(r => !PAGES[r.dataset.id]);
function selectableView() { const t = viewType(); return t === "list" || t === "folder"; }
function paintSelection() { els.forEach((li, id) => li.classList.toggle("selected", selected.has(id))); }
function clearSelection() { if (!selected.size) return false; selected.clear(); anchor = null; paintSelection(); return true; }
function selectClick(id, shift, multi) {
  const ids = orderableRows().map(r => r.dataset.id);
  if (shift && anchor && ids.includes(anchor)) {
    const a = ids.indexOf(anchor), b = ids.indexOf(id);
    if (!multi) selected = new Set();
    ids.slice(Math.min(a, b), Math.max(a, b) + 1).forEach(x => selected.add(x));
  } else if (multi) {
    if (selected.has(id)) selected.delete(id); else selected.add(id);
    anchor = id;
  } else if (selected.size === 1 && selected.has(id)) {
    return "activate";
  } else { selected = new Set([id]); anchor = id; }
  paintSelection();
  return "selected";
}

// Hovering the line between two items opens a small gap with a + to add there.
function setGap(b) {
  if (b === gapAt) return;
  const rows = orderableRows();
  clearTimeout(gapTimer);
  gapAt = b;
  rows.forEach((r, i) => {
    r.classList.add("gap-anim");
    r.classList.toggle("gap-up", b != null && i < b);
    r.classList.toggle("gap-down", b != null && i >= b);
  });
  if (b == null) {
    insertEl.classList.remove("show");
    gapTimer = setTimeout(() => rows.forEach(r => r.classList.remove("gap-anim")), 200);
    return;
  }
  insertEl.style.top = (activeUl.offsetTop + rowEdges(rows)[b]) + "px";
  insertEl.classList.add("show");
}
main.addEventListener("pointermove", e => {
  if (e.pointerType !== "mouse" || g) return;
  if (!selectableView() || document.querySelector(".row.editing")) { setGap(null); return; }
  const rows = orderableRows(), k = rows.length;
  const rect = activeUl.getBoundingClientRect(), y = e.clientY - rect.top;
  if (k < 2 || e.clientX < rect.left || e.clientX > rect.right) { setGap(null); return; }
  const edges = rowEdges(rows), b = nearestEdge(edges, y), dist = Math.abs(y - edges[b]);
  setGap(b >= 1 && b <= k - 1 && dist <= (gapAt === b ? 10 : 6) ? b : null);
});
main.addEventListener("pointerleave", () => { if (!g) setGap(null); });
insertEl.addEventListener("click", e => {
  e.stopPropagation();
  if (gapAt == null || insertEl.classList.contains("drop")) return;
  const before = orderableRows()[gapAt];
  setGap(null); clearSelection();
  if (before) addEntry("before", "list", before.dataset.id);
});

// Press on an item and drag: moves it, or the whole selection, as one block.
function startMultiDrag() {
  if (!selected.has(g.id)) {
    if (!g.multi) selected = new Set();
    selected.add(g.id); anchor = g.id; paintSelection();
  }
  g.lock = "m";
  g.rows = orderableRows();
  g.ids = g.rows.map(r => r.dataset.id).filter(id => selected.has(id));
  g.to = null;
  g.rows.forEach(r => r.classList.toggle("sel-dragging", selected.has(r.dataset.id)));
  const first = entry(g.ids[0]);
  ghost.querySelector(".t").textContent = first ? (viewType() === "list" ? first.text.trim() : first.title) : "";
  ghost.querySelector(".n").textContent = g.ids.length > 1 ? "+" + (g.ids.length - 1) : "";
  ghost.classList.add("show");
  insertEl.classList.add("show", "drop");
  document.body.classList.add("grabbing");
  try { g.li.setPointerCapture(g.pid); } catch (err) {}
}
function moveMultiDrag(e) {
  ghost.style.transform = "translate(" + (e.clientX + 14) + "px," + (e.clientY + 12) + "px)";
  const edges = rowEdges(g.rows);
  const b = nearestEdge(edges, e.clientY - activeUl.getBoundingClientRect().top);
  g.to = b;
  insertEl.style.top = (activeUl.offsetTop + edges[b]) + "px";
  const r = main.getBoundingClientRect();
  if (e.clientY < r.top + 40) main.scrollTop -= 12;
  else if (e.clientY > r.bottom - 40) main.scrollTop += 12;
}
function endMultiDrag(c, cancelled) {
  ghost.classList.remove("show");
  insertEl.classList.remove("show", "drop");
  document.body.classList.remove("grabbing");
  c.rows.forEach(r => r.classList.remove("sel-dragging"));
  if (cancelled || c.to == null) { flushRender(); return; }
  const all = c.rows.map(r => r.dataset.id), moving = new Set(c.ids);
  const before = all.slice(0, c.to).filter(id => !moving.has(id)).length;
  const rest = all.filter(id => !moving.has(id));
  const order = rest.slice(0, before).concat(c.ids, rest.slice(before));
  if (order.join() === all.join()) { flushRender(); return; }
  applyOrder(order);
}
function applyOrder(order) {
  const n = node(), set = new Set(order);
  if (n.type === "list") {
    const byId = new Map(n.items.map(i => [i.id, i]));
    n.items = order.map(id => byId.get(id)).concat(n.items.filter(i => !set.has(i.id)));
  } else {
    n.children = order.concat(n.children.filter(id => !set.has(id)));
  }
  n.orderAt = now();
  buzz(); save(); render();
}

// Acting on a selection from the keyboard.
function removeMany(ids) {
  const n = node(), t = now(), flipped = [];
  let label = "";
  ids.forEach(id => {
    if (n.type === "list") {
      const it = findItem(id);
      if (it && !it.deleted) { it.deleted = true; it.updatedAt = t; flipped.push({ list: n.id, item: id }); label = it.text; }
    } else if (!PAGES[id] && N()[id]) { label = N()[id].title; tombstone(id, t, flipped); }
  });
  if (!flipped.length) return;
  selected.clear(); anchor = null;
  save(); render({ flip: false });
  if (ids.length === 1) offerUndo(flipped, label); else showToast("Deleted " + ids.length + " items", flipped);
}
function completeMany(ids) {
  const t = now();
  ids.forEach(id => { const it = findItem(id); if (it && !it.done) { it.done = true; it.doneAt = t; it.updatedAt = t; } });
  selected.clear(); anchor = null; save(); render();
}

/* Long-press to reorder (the Settings row stays put at the bottom) */
function startReorder() {
  if (!g || g.lock) return;
  g.lock = "r"; buzz();
  const rows = Array.from(activeUl.children).filter(r => !PAGES[r.dataset.id]);
  g.rows = rows; g.from = rows.indexOf(g.li); g.to = g.from;
  g.li.classList.add("lifted");
  rows.forEach(r => { if (r !== g.li) r.classList.add("shift"); });
  try { g.li.setPointerCapture(g.pid); } catch (err) {}
}
function moveReorder() {
  const li = g.li, rows = g.rows, from = g.from, h = li.offsetHeight;
  const d = Math.max(-from * h, Math.min((rows.length - 1 - from) * h, g.dy));
  li.style.transform = "translateY(" + d + "px)";
  const to = Math.round(from + d / h);
  if (to !== g.to) buzz();
  g.to = to;
  rows.forEach((r, i) => {
    if (r === li) return;
    let s = 0;
    if (from < to && i > from && i <= to) s = -h;
    else if (from > to && i >= to && i < from) s = h;
    r.style.transform = s ? "translateY(" + s + "px)" : "";
  });
}
function endReorder(c) {
  const li = c.li, rows = c.rows, from = c.from, to = c.to;
  li.classList.add("shift");
  li.style.transform = "translateY(" + (to - from) * li.offsetHeight + "px)";
  li.classList.remove("lifted");
  setTimeout(() => {
    rows.forEach(r => { r.classList.remove("shift"); r.style.transition = "none"; r.style.transform = ""; });
    if (to !== from) {
      const n = node(), movedId = rows[from].dataset.id, targetId = rows[to].dataset.id;
      if (n.type === "list") {
        const moved = n.items.find(i => i.id === movedId), target = n.items.find(i => i.id === targetId);
        n.items.splice(n.items.indexOf(moved), 1);
        n.items.splice(n.items.indexOf(target) + (to > from ? 1 : 0), 0, moved);
      } else {
        n.children.splice(n.children.indexOf(movedId), 1);
        n.children.splice(n.children.indexOf(targetId) + (to > from ? 1 : 0), 0, movedId);
      }
      n.orderAt = now();
      save();
    }
    render({ flip: false });
    requestAnimationFrame(() => rows.forEach(r => { r.style.transition = ""; }));
  }, 200);
}

/* Pull to create: one row for a task or list, two rows for a folder */
let pullStage = null;
function stageFor(h) {
  const r = rowH(), t = viewType();
  if (t === "settings") return null;
  if (t === "list") return h >= r ? "item" : null;
  return h >= r * 2 ? "folder" : h >= r ? "list" : null;
}
function setPull(h, animate) {
  const t = viewType();
  if (t === "settings") h = 0;
  const r = rowH(), max = r * (t === "list" ? 1.6 : 2.6);
  pullEl.style.transition = animate ? "height .2s ease" : "none";
  pullEl.style.height = Math.min(h, max) + "px";
  const s = stageFor(h);
  if (s !== pullStage) { if (s) buzz(); pullStage = s; }
  pullText.textContent = t === "list"
    ? (s ? "Release to create item" : "Pull to create item")
    : s === "folder" ? "Release to create folder"
    : s === "list" ? "Release for a list, pull for a folder"
    : "Pull to create list";
}
function releasePull(h) {
  const s = stageFor(h);
  setPull(0, true); pullStage = null;
  if (s) addEntry("top", s === "folder" ? "folder" : "list");
}
let pull = null;
main.addEventListener("touchstart", e => {
  pull = (main.scrollTop <= 0 && e.touches.length === 1 && viewType() !== "settings") ? { y0: e.touches[0].clientY, d: 0, on: false } : null;
}, { passive: true });
main.addEventListener("touchmove", e => {
  if (g && (g.lock === "h" || g.lock === "r")) { e.preventDefault(); return; }
  if (!pull) return;
  const d = e.touches[0].clientY - pull.y0;
  if (d > 0 && main.scrollTop <= 0) {
    if (e.cancelable) e.preventDefault();
    pull.on = true; pull.d = d * 0.5; setPull(pull.d);
    if (g) clearTimeout(g.lp);
  } else if (pull.on) { pull.d = 0; setPull(0); }
}, { passive: false });
main.addEventListener("touchend", () => {
  if (pull && pull.on) releasePull(pull.d);
  pull = null;
});
main.addEventListener("contextmenu", e => { if (e.target.closest(".row")) e.preventDefault(); });

/* ---------- Keyboard ---------- */
document.addEventListener("keydown", e => {
  if (e.target.tagName === "INPUT") return;
  if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "a" && selectableView()) {
    e.preventDefault();
    const ids = orderableRows().map(r => r.dataset.id);
    selected = new Set(ids); anchor = ids[0] || null; paintSelection();
    return;
  }
  if (e.metaKey || e.ctrlKey || e.altKey) return;
  const li = document.activeElement && document.activeElement.closest ? document.activeElement.closest(".row") : null;
  const t = viewType();
  if (e.key === "n" && t !== "settings") { e.preventDefault(); addEntry("top", "list"); return; }
  if (e.key === "f" && t === "folder") { e.preventDefault(); addEntry("top", "folder"); return; }
  if (e.key === "Escape") { e.preventDefault(); if (!clearSelection()) goUp(); return; }
  if (selected.size && (e.key === "Delete" || e.key === "Backspace")) { e.preventDefault(); removeMany([...selected]); return; }
  if (selected.size && e.key === " " && t === "list") { e.preventDefault(); completeMany([...selected]); return; }
  if (selected.size === 1 && e.key === "Enter" && !li) {
    e.preventDefault(); const id = [...selected][0];
    if (t === "list") startEdit(id); else go(id);
    return;
  }
  if (!li) return;
  const id = li.dataset.id;
  if (e.key === "Enter") {
    e.preventDefault();
    if (t === "settings") handlePref(id);
    else if (PAGES[id]) go(id);
    else if (t === "list") startEdit(id); else go(id);
  }
  else if (e.key === "F2" && !isFixedRow(id)) { e.preventDefault(); startEdit(id); }
  else if (e.key === " " && t === "list") { e.preventDefault(); toggleDone(id); setTimeout(() => { const n = els.get(id); if (n) n.focus(); }, 0); }
  else if ((e.key === "Delete" || e.key === "Backspace") && !isFixedRow(id)) { e.preventDefault(); removeEntry(id); }
  else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
    e.preventDefault();
    const all = Array.from(document.querySelectorAll(".row"));
    const next = all[all.indexOf(li) + (e.key === "ArrowDown" ? 1 : -1)];
    if (next) next.focus();
  }
});

/* ---------- Start ---------- */
const start = idFromHash();
if (start) { cur = start; if (!PAGES[start]) state.current = start; }
saveLocal();
render({ flip: false });
// Coming back from Google: finish sign-in before the address is tidied up.
finishSignIn().then(fromAuth => {
  if (fromAuth) { cur = "settings"; clearRows(); render({ flip: false }); }
  if (location.hash !== hashFor(cur)) history.replaceState(null, "", hashFor(cur));
  if (!fromAuth && !PREVIEW) checkAccount();
  scheduleSync(300);
});

if ("serviceWorker" in navigator && (location.protocol === "https:" || ["localhost", "127.0.0.1"].includes(location.hostname))) {
  navigator.serviceWorker.register("./sw.js").catch(() => {});
}
